


// pages/UserProfilePage.tsx
import React, { useState } from 'react';
import { useCurrentUser } from '@/hooks/useUser';
import { getFirebaseAuth } from '@bookstore/shared/browser';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Edit3,
  Camera,
  ChevronRight,
  LogOut,
  ShoppingBag,
  Heart,
  Bell,
  Shield,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ProfileSkeleton from '@/components/ui/ProfileSkeleton';
import EditProfileModal from '@/components/ui/EditProfileModal';
import { format } from 'date-fns';

const ProfilePage = () => {
  const auth = getFirebaseAuth();
  const navigate = useNavigate();
  const { data: userProfile, isLoading, isFetching } = useCurrentUser();
  const [showEditModal, setShowEditModal] = useState(false);
  // const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      toast.success('Logged out successfully');
      navigate('/');
    } catch {
      toast.error('Failed to log out');
    }
  };

  // const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;

  //   if (!file.type.startsWith('image/')) return toast.error('Please upload an image file');
  //   if (file.size > 5 * 1024 * 1024) return toast.error('Image size should be less than 5MB');

  //   setUploadingPhoto(true);
  //   toast.success('Photo upload feature coming soon!');
  //   setUploadingPhoto(false);
  // };

  if (isLoading) return <ProfileSkeleton />;

  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-text-secondary mb-4">Unable to load profile</p>
          <Link to="/" className="text-primary-600 hover:underline" aria-label="homepage">Go to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative pb-24">

      {/* Blurred overlay while editing */}
      {isFetching && (
        <div className="absolute inset-0 bg-background-soft/30 backdrop-blur-[2px] z-10 flex items-center justify-center">
          <div className="bg-white rounded-xl border border-primary-light p-5 shadow-sm">
            <div className="h-8 w-8 border-4 border-primary-light border-t-primary-600 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-[14px] text-text-secondary">Updating profile...</p>
          </div>
        </div>
      )}

      <div className="container-custom max-w-4xl mx-auto px-4 py-10">
        
        {/* HEADER CARD */}
        <div className="relative bg-background-soft/30 rounded-3xl border border-[rgba(45,45,45,0.08)] p-10 shadow-[0_0_0_1px_rgba(0,0,0,0.03)] overflow-hidden">

          {/* Paper texture glow */}
          <div className="absolute top-[-80px] right-[-70px] w-[280px] h-[280px] bg-primary-light rounded-full blur-[90px] opacity-60"></div>

          <div className="relative flex flex-col md:flex-row items-center gap-8">

            {/* PHOTO */}
            <div className="relative group">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-background border-2 border-primary flex items-center justify-center bg-primary-100">
                {userProfile.photoURL ? (
                  <img src={userProfile.photoURL} className="object-cover w-full h-full" />
                ) : (
                  <span className="text-4xl font-light text-primary-700">
                    {userProfile?.displayName?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* <label className="absolute bottom-1 right-1 w-10 h-10 bg-background rounded-full border border-primary flex items-center justify-center cursor-pointer hover:bg-background-muted  transition">
                <Camera className="w-5 h-5 text-primary " />
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
              </label> */}
            </div>

            {/* USER INFO */}
            <div className="text-center md:text-left flex-1">
              <h1 className="text-[28px] font-medium text-text leading-tight">{userProfile.displayName}</h1>
              <p className="text-text-secondary mt-1">{userProfile.email}</p>

              <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-[14px]">
                  <Shield className="w-4 h-4" />
                  {userProfile.role === 'admin' ? 'Administrator' : 'Member'}
                </span>

                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-soft text-text-secondary text-[14px]">
                  <Calendar className="w-4 h-4" />
                  Joined {format(userProfile.createdAt.toDate(), 'MMM yyyy')}
                </span>
              </div>
            </div>

            {/* EDIT BUTTON */}
            <button
              onClick={() => setShowEditModal(true)}
              className="absolute top-4 right-4 md:static p-3 rounded-full hover:bg-primary-light transition"
            >
              <Edit3 className="w-5 h-5 text-text" />
            </button>
          </div>
        </div>

        {/* INFO SECTIONS */}
        <div className="space-y-8 mt-8  ">

          {/* PERSONAL INFO */}
          <Card title="Personal Information">
            <InfoRow icon={<User />} label="Full Name" value={userProfile.displayName} />
            <InfoRow icon={<Mail />} label="Email" value={userProfile.email} />
            <InfoRow icon={<Phone />} label="Phone" value={userProfile.phoneNumber || 'Not added'} isEmpty={!userProfile.phoneNumber} />
          </Card>

          {/* QUICK ACTIONS */}
          <Card title="Quick Actions">
            <ActionRow icon={<ShoppingBag />} label="My Orders" to="/orders" />
            <ActionRow icon={<Heart />} label="Wishlist" to="/wishlist" />
            <ActionRow icon={<Bell />} label="Notifications" to="/notifications" />
          </Card>

          {/* LOGOUT */}
          <button
            onClick={handleLogout}
            className="w-full rounded-md border border-[rgba(45,45,45,0.1)] p-6 flex items-center bg-primary text-white justify-center gap-3 text-error-600 hover:bg-error-50 transition text-[16px]"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </div>

      <EditProfileModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} userProfile={userProfile} />
    </div>
  );
};

/* ----------------- COMPONENTS ------------------ */

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-background-soft/30 rounded-3xl border border-[rgba(45,45,45,0.08)] overflow-hidden shadow-sm">
    <div className="px-6 py-4 border-b border-[rgba(45,45,45,0.08)]">
      <h2 className="text-[18px] text-text font-medium">{title}</h2>
    </div>
    <div className="divide-y divide-[rgba(45,45,45,0.06)]">{children}</div>
  </div>
);

const InfoRow = ({
  icon,
  label,
  value,
  isEmpty,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  isEmpty?: boolean;
}) => (
  <div className="px-6 py-4 flex items-center gap-4 hover:bg-primary-light transition">
    <div className="w-10 h-10 rounded-full bg-background-soft flex items-center justify-center text-primary">
      {icon}
    </div>

    <div className="flex-1">
      <p className="text-[14px] text-primary">{label}</p>
      <p className={`text-[16px] font-medium ${isEmpty ? 'text-text-secondary/50 italic' : 'text-text'}`}>{value}</p>
    </div>
  </div>
);

const ActionRow = ({
  icon,
  label,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
}) => (
  <Link
    to={to}
    className="px-6 py-4 flex items-center gap-4 hover:bg-primary-light/20 transition group"
  >
    <div className="w-10 h-10 rounded-full bg-background-soft flex items-center justify-center text-primary-700">
      {icon}
    </div>
    <span className="flex-1 text-[16px] text-text font-medium">{label}</span>
    <ChevronRight className="w-5 h-5 text-text-secondary group-hover:translate-x-1 transition" />
  </Link>
);

export default ProfilePage;
